import { ApiResponse } from '@nohara-dxdev/oidc-core';
import {
  PaginatedQueryConfig,
  PaginationParams,
  PaginationResponse,
  usePaginatedQuery,
  UsePaginatedQueryOptions,
} from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';

import { api } from '@/lib/api-client';
import { ItemTemplateEntityWithSchema } from '@/types/api';

import {
  convertItemTemplateFilterToApiParams,
  ItemTemplateFilterValues,
} from '../utils/convert-item-template-filter-to-api-params';

export interface ItemTemplateSearchParams extends PaginationParams, ItemTemplateFilterValues {}

export const getItemTemplateSchemas = async (
  orgId: string,
  params?: ItemTemplateSearchParams,
): Promise<PaginationResponse<ItemTemplateEntityWithSchema>> => {
  // Extract pagination params
  const { limit, cursor, ...filterValues } = params ?? {};

  // Convert filter values to API params
  const filterParams = convertItemTemplateFilterToApiParams(filterValues);

  const { data } = await api.get<
    undefined,
    ApiResponse<PaginationResponse<ItemTemplateEntityWithSchema>>
  >(`/orgs/${orgId}/item-templates/schemas`, {
    params: {
      ...filterParams,
      limit,
      cursor,
    },
  });
  return data;
};

export const getItemTemplateSchemasQueryKey = (
  orgId?: string | null,
  searchParams?: Omit<ItemTemplateSearchParams, 'limit' | 'cursor'>,
) => {
  const queryKey = ['item-template-schemas'];
  if (orgId) queryKey.push(orgId);
  if (searchParams) queryKey.push(JSON.stringify(searchParams));
  return queryKey;
};

export const getItemTemplateSchemasQueryOptions = (
  orgId: string | undefined | null,
  searchParams?: Omit<ItemTemplateSearchParams, 'limit' | 'cursor'>,
): UsePaginatedQueryOptions<ItemTemplateEntityWithSchema> => {
  return {
    queryKey: getItemTemplateSchemasQueryKey(orgId, searchParams),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: ({ pageParam }) => getItemTemplateSchemas(orgId!, { ...searchParams, ...pageParam }),
    enabled: !!orgId,
  };
};

interface UseItemTemplateSchemaOptions extends ItemTemplateFilterValues {
  queryConfig?: PaginatedQueryConfig<typeof getItemTemplateSchemasQueryOptions>;
}

export const useItemTemplateSchemas = ({
  queryConfig,
  ...filterValues
}: UseItemTemplateSchemaOptions = {}) => {
  const { orgId } = useScopeIds('org');

  const searchParams: ItemTemplateSearchParams = filterValues;

  return usePaginatedQuery({
    ...getItemTemplateSchemasQueryOptions(orgId, searchParams),
    ...queryConfig,
  });
};
